/**
 * @fileoverview Public program page (/programs/:programId). Shows one
 * program's details and its member instructors; each bookable instructor gets
 * a "Book a lesson" link into the program-scoped booking flow
 * (/programs/:programId/book/:userId → BookingPage).
 *
 * Bare axios instance — public page, no auth interceptor.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { LESSON_TYPE_LABELS } from "@/lib/schedule";
import BACKEND_URL from "@/lib/backendUrl";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const shellClass = "min-h-screen px-6 py-14";
const shellStyle = {
  background:
    "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #08080a",
};

function InstructorCard({ programId, instructor }) {
  const [photoBroken, setPhotoBroken] = useState(false);
  return (
    <div
      className="flex items-start gap-4 rounded-2xl border border-white/10 bg-zinc-900/60 p-5"
      data-testid={`program-instructor-${instructor.user_id}`}
    >
      {instructor.photo_url && !photoBroken ? (
        <img
          src={instructor.photo_url}
          alt={`${instructor.name}'s photo`}
          onError={() => setPhotoBroken(true)}
          className="h-14 w-14 shrink-0 rounded-full border border-white/10 object-cover"
        />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/40 font-serif text-lg text-zinc-400">
          {(instructor.name || "?").charAt(0)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="font-sans text-base font-semibold text-white">{instructor.name}</h3>
        {instructor.lesson_types?.length > 0 && (
          <p className="mt-0.5 font-sans text-[12px] text-zinc-500">
            {instructor.lesson_types.map((t) => LESSON_TYPE_LABELS[t] || t).join(" · ")}
          </p>
        )}
        {instructor.bio && <p className="mt-1.5 font-sans text-sm leading-relaxed text-zinc-400">{instructor.bio}</p>}
        <div className="mt-3">
          {instructor.bookable ? (
            <Link
              to={`/programs/${programId}/book/${instructor.user_id}`}
              data-testid={`program-book-${instructor.user_id}`}
              className="inline-block rounded-full bg-[#f4cf08] px-5 py-2 font-sans text-sm font-semibold text-black"
            >
              Book a lesson
            </Link>
          ) : (
            <span className="font-sans text-[13px] text-zinc-600">Contact the organization to book</span>
          )}
        </div>
      </div>
    </div>
  );
}

const ProgramPage = () => {
  const { programId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/programs/${programId}`)
      .then((res) => setData(res.data))
      .catch((err) =>
        setError(
          err.response?.status === 404
            ? "This program isn't available anymore."
            : "Could not load the program — try again in a moment."
        )
      );
  }, [programId]);

  if (error) {
    return (
      <div
        className={shellClass}
        style={shellStyle}
        data-theme="dark"
      >
        <div className="mx-auto max-w-md text-center">
          <p className="font-sans text-base text-zinc-400" data-testid="program-error">
            {error}
          </p>
          <Link to="/programs" className="mt-4 inline-block font-sans text-sm text-[#f4cf08] underline">
            Browse all programs
          </Link>
        </div>
      </div>
    );
  }

  const program = data?.program;
  const branding = data?.branding; // Spec 12 WP3: owner-org public brand
  const accent = branding?.accent_color || "#f4cf08";
  return (
    <div
      className={shellClass}
      style={shellStyle}
      data-theme="dark"
    >
      <div className="mx-auto max-w-2xl" data-testid="program-page">
        <Link to="/programs" className="font-sans text-[13px] text-zinc-500 hover:text-zinc-300">
          ← All programs
        </Link>
        <div className="mb-8 mt-4">
          <div className="flex items-center gap-2">
            {branding?.logo_url && (
              <img src={branding.logo_url} alt="" className="h-6 w-6 rounded object-cover" data-testid="program-org-logo" />
            )}
            <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: accent }}>
              {branding?.org_name || program?.org_name || ""}
            </p>
          </div>
          <h1 className="mt-2 font-serif text-[34px] font-normal tracking-[-0.01em] text-white">
            {program ? program.title : "Loading…"}
          </h1>
          {program?.description && (
            <p className="mt-3 font-sans text-sm leading-relaxed text-zinc-400">{program.description}</p>
          )}
          {program && (
            <p className="mt-2 font-sans text-[13px] text-zinc-500">
              {[(program.instruments || []).join(", "), program.location, program.start_date ? `Starts ${program.start_date}` : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>

        {data && (
          <>
            <h2 className="mb-3 font-sans text-[13px] font-semibold uppercase tracking-wide text-zinc-500">
              Instructors
            </h2>
            {data.instructors.length === 0 ? (
              <p className="font-sans text-sm text-zinc-500">No instructors are listed yet.</p>
            ) : (
              <div className="space-y-4">
                {data.instructors.map((instructor) => (
                  <InstructorCard key={instructor.user_id} programId={programId} instructor={instructor} />
                ))}
              </div>
            )}
          </>
        )}
        {branding?.powered_by_hans !== false && (
          <p className="mt-10 text-center font-sans text-[11px] text-zinc-600" data-testid="powered-by-hans">
            Powered by Hans
          </p>
        )}
      </div>
    </div>
  );
};

export default ProgramPage;
